/* @flow */

import * as React from 'react'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import styled, { css } from 'styled-components'

import chunk from 'lodash/chunk'
import uniq from 'lodash/uniq'

export const ColorDots = ({ colorDots, showMaxColors, twoLinesThreshold }) => {
  const chunkedColorDots = React.useMemo(() => {
    if (colorDots.length < twoLinesThreshold) {
      return []
    }

    return chunk(
      colorDots.slice(0, showMaxColors),
      Math.ceil(colorDots.length / 2)
    )
  }, [colorDots, showMaxColors, twoLinesThreshold])

  if (colorDots.length == 0) {
    return null
  }

  return (
    <ColorsContainer>
      {colorDots.length < twoLinesThreshold && (
        <ColorsRow>
          {colorDots.map((color, colorIndex) => (
            <ColorDotWithTooltip key={colorIndex} color={color} />
          ))}
        </ColorsRow>
      )}
      {colorDots.length >= twoLinesThreshold &&
        chunkedColorDots.map((part, partIndex) => (
          <ColorsRow key={partIndex}>
            {part.map((color, colorIndex) => (
              <ColorDotWithTooltip key={colorIndex} color={color} />
            ))}
          </ColorsRow>
        ))}
    </ColorsContainer>
  )
}

const ColorsContainer = styled.div`
  margin: 10px 0;
`

const ColorsRow = styled.div`
  font-size: 10px;
`

const ColorDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  margin: 0 3.5px;
  border-radius: 4px;
  background-color: ${props => props.hex};
  vertical-align: middle;

  ${props =>
    props.hex.toLowerCase() === '#ffffff' &&
    css`
      border: 1px solid #d3d3d3;
    `}
`

const ColorDotWithTooltip = ({ color }) => (
  <OverlayTrigger
    placement="top"
    overlay={
      <Tooltip id="webshop-search-product-color-tooltip">{color.name}</Tooltip>
    }
  >
    <ColorDot hex={color.hex} />
  </OverlayTrigger>
)

export const useCalculatePreviewImagesAndColorDots = (
  product,
  colorAttributeName,
  colorAttributeColors,
  maxPreviewImages
) => {
  return React.useMemo(() => {
    if (!product) {
      return [[], {}, []]
    }

    let previewImages: Array<string> = [product.image]
    let imagesColor: { [string]: string } = {}
    let colorDots: Array<{ name: string, hex: string }> = []

    const colorAttribute = product.attributes.find(
      attr => attr === colorAttributeName
    )
    if (colorAttribute) {
      const colors = uniq(
        product.variants.map(variant =>
          getVariantColor(variant, colorAttribute)
        )
      )

      colorDots = colors
        .map(color => ({ name: color, hex: colorAttributeColors[color] }))
        .filter(color => !!color.hex)

      colors.forEach(color => {
        // Try to find variant with picture for that color
        const found = product.variants.find(
          variant =>
            color === getVariantColor(variant, colorAttribute) &&
            variant.picture
        )

        if (found) {
          previewImages = [...previewImages, found.picture]
        }
      })

      product.variants.forEach(variant => {
        imagesColor[variant.picture] = getVariantColor(variant, colorAttribute)
      })
    }

    previewImages = [...previewImages, ...product.images]
    previewImages = uniq(previewImages)
    previewImages = previewImages.slice(0, maxPreviewImages)

    return [previewImages, imagesColor, colorDots]
  }, [product, colorAttributeName, maxPreviewImages])
}

const getVariantColor = (variant, colorAttribute) =>
  variant.attributes[colorAttribute]
